<script setup>
import { ref, onMounted, useId } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import ReportPeriodFilter from 'dashboard/components-next/polaris/reports/ReportPeriodFilter.vue';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';
import { Label } from 'dashboard/components-next/polaris/ui/label';
import {
  generateReportURLParams,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const route = useRoute();
const router = useRouter();

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const businessHoursSelected = ref(false);
const businessHoursId = useId();

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
    range: selectedDateRange.value,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: getUnixStartOfDay(customDateRange.value[0]),
    to: getUnixEndOfDay(customDateRange.value[1]),
    businessHours: businessHoursSelected.value,
  });
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Set the range type first
  if (urlParams.range) {
    selectedDateRange.value = urlParams.range;
  }

  // Restore dates from URL if available
  if (urlParams.from && urlParams.to) {
    customDateRange.value = [
      new Date(urlParams.from * 1000),
      new Date(urlParams.to * 1000),
    ];
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }
};

onMounted(() => {
  initializeFromURL();
  emitChange();
});
</script>

<!-- The lists' bar (R6): period and business hours, as today (no "+ Filter": the lists have none). -->
<template>
  <div
    class="flex w-full flex-wrap items-center gap-2"
    :class="{ 'pointer-events-none opacity-50': disabled }"
    role="group"
    :aria-label="$t('POLARIS_REPORTS.FILTERS.LABEL')"
    data-report-filters
  >
    <ReportPeriodFilter
      v-model:date-range="customDateRange"
      v-model:range-type="selectedDateRange"
      @date-range-changed="onDateRangeChange"
    />
    <div
      class="flex shrink-0 items-center gap-2 ms-auto"
      data-report-business-hours
    >
      <Label :for="businessHoursId" class="whitespace-nowrap">
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </Label>
      <Switch
        :id="businessHoursId"
        v-model="businessHoursSelected"
        @update:model-value="onBusinessHoursToggle"
      />
    </div>
  </div>
</template>
